import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  Input,
  Modal,
  Textarea,
  useToast,
} from '@adop/ui';
import { CalendarClock, Pause, Plus, Trash2 } from 'lucide-react';
import { localParts } from '@adop/shared';
import { formatDate } from '../../lib/format';
import { useWorkspace } from '../../lib/workspace-context';
import { useAsync } from '../../lib/useAsync';
import {
  deleteSpecialHours,
  listOpeningHours,
  listSpecialHours,
  replaceOpeningHours,
  updateLocation,
  upsertSpecialHours,
  type SpecialHoursRow,
} from '../../lib/data';
import { errorMessage } from '../../lib/api';
import { Alert } from '../../components/Alert';
import { PageHeader } from '../../components/PageHeader';
import { ListSkeleton } from '../../components/LoadingState';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const PAUSE_KEY = 'adop.platform.orderPause';

interface Range {
  opens: string;
  closes: string;
}

type WeekRanges = Record<number, Range[]>;

function emptyWeek(): WeekRanges {
  return { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
}

interface PauseRecord {
  locationId: string;
  until: number;
}

function readPause(): PauseRecord | null {
  try {
    const raw = window.localStorage.getItem(PAUSE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PauseRecord>;
    if (typeof parsed.locationId !== 'string' || typeof parsed.until !== 'number') return null;
    return { locationId: parsed.locationId, until: parsed.until };
  } catch {
    return null;
  }
}

function writePause(record: PauseRecord | null): void {
  try {
    if (record) window.localStorage.setItem(PAUSE_KEY, JSON.stringify(record));
    else window.localStorage.removeItem(PAUSE_KEY);
  } catch {
    // ignore
  }
}

export function HoursPage() {
  const { location, canManage } = useWorkspace();
  const { toast } = useToast();
  const [week, setWeek] = useState<WeekRanges>(emptyWeek);
  const [saving, setSaving] = useState(false);
  const [specialForm, setSpecialForm] = useState<{
    id: string | null;
    date: string;
    closed: boolean;
    opens: string;
    closes: string;
    note: string;
  } | null>(null);
  const [deleteSpecial, setDeleteSpecial] = useState<SpecialHoursRow | null>(null);
  const [pauseUntil, setPauseUntil] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());

  const hoursState = useAsync(() => (location ? listOpeningHours(location.id) : Promise.resolve([])), [
    location?.id,
  ]);
  const specialState = useAsync(() => (location ? listSpecialHours(location.id) : Promise.resolve([])), [
    location?.id,
  ]);

  useEffect(() => {
    const grouped = emptyWeek();
    for (const row of hoursState.data ?? []) {
      const bucket = grouped[row.weekday] ?? [];
      bucket.push({ opens: row.opens.slice(0, 5), closes: row.closes.slice(0, 5) });
      grouped[row.weekday] = bucket;
    }
    setWeek(grouped);
  }, [hoursState.data]);

  // Restore the 30-minute pause after a reload.
  useEffect(() => {
    if (!location) return;
    const record = readPause();
    if (!record || record.locationId !== location.id) return;
    if (record.until <= Date.now()) {
      writePause(null);
      void updateLocation(location.id, { ordering_enabled: true }).catch(() => undefined);
    } else {
      setPauseUntil(record.until);
    }
  }, [location]);

  useEffect(() => {
    if (pauseUntil === null) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [pauseUntil]);

  useEffect(() => {
    if (pauseUntil !== null && pauseUntil <= now && location) {
      setPauseUntil(null);
      writePause(null);
      void updateLocation(location.id, { ordering_enabled: true }).then(() => {
        toast({ title: 'Online ordering is back on', variant: 'success' });
      });
    }
  }, [pauseUntil, now, location, toast]);

  const setRange = (weekday: number, index: number, patch: Partial<Range>) => {
    setWeek((current) => {
      const ranges = [...(current[weekday] ?? [])];
      const existing = ranges[index];
      if (!existing) return current;
      ranges[index] = { ...existing, ...patch };
      return { ...current, [weekday]: ranges };
    });
  };

  const addRange = (weekday: number) => {
    setWeek((current) => ({
      ...current,
      [weekday]: [...(current[weekday] ?? []), { opens: '11:00', closes: '22:00' }],
    }));
  };

  const removeRange = (weekday: number, index: number) => {
    setWeek((current) => ({
      ...current,
      [weekday]: (current[weekday] ?? []).filter((_, position) => position !== index),
    }));
  };

  const saveWeek = async () => {
    if (!location) return;
    setSaving(true);
    try {
      const ranges = Object.entries(week).flatMap(([weekday, list]) =>
        list.map((range) => ({ weekday: Number(weekday), opens: range.opens, closes: range.closes })),
      );
      await replaceOpeningHours(
        { restaurantId: location.restaurant_id, locationId: location.id },
        ranges,
      );
      toast({ title: 'Hours saved', variant: 'success' });
      hoursState.reload();
    } catch (cause) {
      toast({ title: 'Could not save hours', description: errorMessage(cause), variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const saveSpecial = async () => {
    if (!specialForm || !location) return;
    try {
      await upsertSpecialHours(
        { restaurantId: location.restaurant_id, locationId: location.id },
        {
          id: specialForm.id ?? undefined,
          date: specialForm.date,
          closed: specialForm.closed,
          opens: specialForm.closed ? null : specialForm.opens,
          closes: specialForm.closed ? null : specialForm.closes,
          note: specialForm.note.trim() || null,
        },
      );
      toast({ title: 'Special hours saved', variant: 'success' });
      setSpecialForm(null);
      specialState.reload();
    } catch (cause) {
      toast({ title: 'Could not save', description: errorMessage(cause), variant: 'error' });
    }
  };

  const removeSpecial = async () => {
    if (!deleteSpecial) return;
    try {
      await deleteSpecialHours(deleteSpecial.id);
      toast({ title: 'Special hours removed', variant: 'success' });
      setDeleteSpecial(null);
      specialState.reload();
    } catch (cause) {
      toast({ title: 'Could not remove', description: errorMessage(cause), variant: 'error' });
    }
  };

  const startPause = useCallback(async () => {
    if (!location) return;
    const until = Date.now() + 30 * 60 * 1000;
    try {
      await updateLocation(location.id, { ordering_enabled: false });
      writePause({ locationId: location.id, until });
      setPauseUntil(until);
      setNow(Date.now());
      toast({ title: 'Online ordering paused for 30 minutes', variant: 'success' });
    } catch (cause) {
      toast({ title: 'Could not pause', description: errorMessage(cause), variant: 'error' });
    }
  }, [location, toast]);

  const resumeNow = async () => {
    if (!location) return;
    try {
      await updateLocation(location.id, { ordering_enabled: true });
      writePause(null);
      setPauseUntil(null);
      toast({ title: 'Online ordering resumed', variant: 'success' });
    } catch (cause) {
      toast({ title: 'Could not resume', description: errorMessage(cause), variant: 'error' });
    }
  };

  const remaining = useMemo(() => {
    if (pauseUntil === null) return null;
    const seconds = Math.max(0, Math.round((pauseUntil - now) / 1000));
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  }, [pauseUntil, now]);

  if (!location) {
    return <EmptyState icon={<CalendarClock className="h-6 w-6" />} title="Select a location" />;
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Hours"
        description={`Opening hours are in ${location.timezone}.`}
        action={
          <div className="flex items-center gap-2">
            {pauseUntil !== null ? (
              <>
                <span className="text-sm text-amber-700">Paused — resumes in {remaining}</span>
                <Button variant="outline" size="sm" onClick={() => void resumeNow()}>
                  Resume now
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                size="sm"
                disabled={!canManage}
                onClick={() => void startPause()}
              >
                <Pause className="h-4 w-4" /> Pause 30 min
              </Button>
            )}
          </div>
        }
      />

      {hoursState.error ? <Alert>{hoursState.error}</Alert> : null}

      <Card>
        <CardHeader>
          <CardTitle>Weekly hours</CardTitle>
        </CardHeader>
        <CardContent>
          {hoursState.loading ? (
            <ListSkeleton rows={3} />
          ) : (
            <div className="space-y-3">
              {WEEKDAYS.map((label, weekday) => (
                <div key={label} className="rounded-lg border border-zinc-200 p-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-zinc-900">{label}</p>
                    {canManage ? (
                      <Button variant="ghost" size="sm" onClick={() => addRange(weekday)}>
                        <Plus className="h-4 w-4" /> Add range
                      </Button>
                    ) : null}
                  </div>
                  {(week[weekday] ?? []).length === 0 ? (
                    <p className="mt-1 text-xs text-zinc-400">Closed</p>
                  ) : (
                    <div className="mt-2 space-y-2">
                      {(week[weekday] ?? []).map((range, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <input
                            type="time"
                            value={range.opens}
                            disabled={!canManage}
                            onChange={(event) => setRange(weekday, index, { opens: event.target.value })}
                            className="rounded-lg border border-zinc-200 px-2 py-1.5 text-sm"
                          />
                          <span className="text-zinc-400">–</span>
                          <input
                            type="time"
                            value={range.closes}
                            disabled={!canManage}
                            onChange={(event) => setRange(weekday, index, { closes: event.target.value })}
                            className="rounded-lg border border-zinc-200 px-2 py-1.5 text-sm"
                          />
                          {canManage ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label="Remove range"
                              onClick={() => removeRange(weekday, index)}
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {canManage ? (
            <div className="mt-4">
              <Button loading={saving} onClick={() => void saveWeek()}>
                Save weekly hours
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Special hours</CardTitle>
            {canManage ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  setSpecialForm({
                    id: null,
                    date: localParts(new Date(), location.timezone).date,
                    closed: true,
                    opens: '11:00',
                    closes: '22:00',
                    note: '',
                  })
                }
              >
                <Plus className="h-4 w-4" /> Add date
              </Button>
            ) : null}
          </div>
        </CardHeader>
        <CardContent>
          {specialState.loading ? (
            <ListSkeleton rows={2} />
          ) : (specialState.data ?? []).length === 0 ? (
            <p className="text-sm text-zinc-500">No special hours. Add holidays or one-off changes.</p>
          ) : (
            <div className="space-y-2">
              {(specialState.data ?? []).map((row) => (
                <div key={row.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-zinc-200 p-3 text-sm">
                  <span className="font-medium text-zinc-900">{formatDate(`${row.date}T12:00:00`)}</span>
                  <span className="text-zinc-600">
                    {row.closed ? 'Closed' : `${row.opens?.slice(0, 5)} – ${row.closes?.slice(0, 5)}`}
                  </span>
                  {row.note ? <span className="text-zinc-500">{row.note}</span> : null}
                  {canManage ? (
                    <div className="ml-auto flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setSpecialForm({
                            id: row.id,
                            date: row.date,
                            closed: row.closed,
                            opens: row.opens?.slice(0, 5) ?? '11:00',
                            closes: row.closes?.slice(0, 5) ?? '22:00',
                            note: row.note ?? '',
                          })
                        }
                      >
                        Edit
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteSpecial(row)}>
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Modal
        open={specialForm !== null}
        onClose={() => setSpecialForm(null)}
        title={specialForm?.id ? 'Edit special hours' : 'Add special hours'}
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setSpecialForm(null)}>
              Cancel
            </Button>
            <Button onClick={() => void saveSpecial()}>Save</Button>
          </>
        }
      >
        {specialForm ? (
          <div className="space-y-4">
            <Input
              label="Date"
              type="date"
              value={specialForm.date}
              onChange={(event) => setSpecialForm({ ...specialForm, date: event.target.value })}
            />
            <Checkbox
              label="Closed all day"
              checked={specialForm.closed}
              onChange={(event) => setSpecialForm({ ...specialForm, closed: event.target.checked })}
            />
            {!specialForm.closed ? (
              <div className="flex items-center gap-2">
                <input
                  type="time"
                  value={specialForm.opens}
                  onChange={(event) => setSpecialForm({ ...specialForm, opens: event.target.value })}
                  className="rounded-lg border border-zinc-200 px-2 py-1.5 text-sm"
                />
                <span className="text-zinc-400">–</span>
                <input
                  type="time"
                  value={specialForm.closes}
                  onChange={(event) => setSpecialForm({ ...specialForm, closes: event.target.value })}
                  className="rounded-lg border border-zinc-200 px-2 py-1.5 text-sm"
                />
              </div>
            ) : null}
            <Textarea
              label="Note"
              rows={2}
              value={specialForm.note}
              onChange={(event) => setSpecialForm({ ...specialForm, note: event.target.value })}
            />
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={deleteSpecial !== null}
        title="Remove special hours?"
        message="The date will use your regular weekly hours again."
        confirmLabel="Remove"
        variant="danger"
        onConfirm={() => void removeSpecial()}
        onCancel={() => setDeleteSpecial(null)}
      />
    </div>
  );
}
